import React, { useEffect, useState } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { useI18n } from '../../hooks/useI18n'
import AddMedicationModal from '../medications/AddMedicationModal'

const MedicationsPage: React.FC = () => {
  const { medications, loading, loadMedications, deleteMedication, adjustStock } = useMedications()
  const { t } = useI18n()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [showStockModal, setShowStockModal] = useState<string | null>(null)
  const [newCurrentStock, setNewCurrentStock] = useState('')
  const [newMaxStock, setNewMaxStock] = useState('')
  const [stockReason, setStockReason] = useState('')
  const [editingMedication, setEditingMedication] = useState<any | null>(null)

  useEffect(() => {
    loadMedications()
  }, [loadMedications])

  const handleDelete = async (medicationId: string) => {
    try {
      await deleteMedication(medicationId)
      setShowDeleteConfirm(null)
    } catch (error) {
      console.error('Error deleting medication:', error)
      // TODO: Show error toast
    }
  }

  const handleStockUpdate = async (medicationId: string) => {
    try {
      const currentStock = parseInt(newCurrentStock)
      const maxStock = parseInt(newMaxStock)
      
      if (isNaN(currentStock) || isNaN(maxStock)) return
      if (currentStock < 0 || maxStock < 0) return
      
      // Find the medication to get current values for adjustment calculation
      const medication = medications.find(m => m.id === medicationId)
      if (!medication) return
      
      const currentStockValue = medication.currentStock || medication.stock || 0
      const adjustment = currentStock - currentStockValue
      
      // Update both current stock and max stock
      await adjustStock(medicationId, adjustment, stockReason, maxStock)
      
      setShowStockModal(null)
      setNewCurrentStock('')
      setNewMaxStock('')
      setStockReason('')
    } catch (error) {
      console.error('Error updating stock:', error)
      // TODO: Show error toast
    }
  }

  const handleEdit = (medication: any) => {
    setEditingMedication(medication)
    setShowAddModal(true)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingMedication(null)
  }

  const getStockStatus = (medication: any) => {
    const currentStock = medication.currentStock || medication.stock || 0
    const maxStock = medication.maxStock || 30
    const lowStockThreshold = medication.lowStockThreshold || Math.ceil(maxStock * 0.2)
    
    if (currentStock <= 0) return 'empty'
    if (currentStock <= lowStockThreshold) return 'low'
    return 'good'
  }

  const getStockColor = (status: string) => {
    switch (status) {
      case 'empty': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400'
      case 'low': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'good': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400'
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const formatDays = (daysOfWeek: number[] | undefined) => {
    if (!daysOfWeek || daysOfWeek.length === 0) return t('all-days')
    if (daysOfWeek.length === 7) return t('all-days')
    
    const dayNames = [
      t('sunday').substring(0, 3),
      t('monday').substring(0, 3),
      t('tuesday').substring(0, 3),
      t('wednesday').substring(0, 3),
      t('thursday').substring(0, 3),
      t('friday').substring(0, 3),
      t('saturday').substring(0, 3)
    ]
    return daysOfWeek.map(day => dayNames[day]).join(', ')
  }

  const formatTimes = (times: string[]) => {
    if (!times || times.length === 0) return t('no-specific-schedule')
    return times.map(time => {
      const [hours, minutes] = time.split(':')
      const hour24 = parseInt(hours)
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
      const ampm = hour24 >= 12 ? 'PM' : 'AM'
      return `${hour12}:${minutes} ${ampm}`
    }).join(', ')
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('medications')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('manage-medications')}
            </p>
          </div>
          <button
            onClick={() => {
              setEditingMedication(null)
              setShowAddModal(true)
            }}
            className="btn btn-primary flex items-center px-4 py-3"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('add-medication')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {medications.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('no-medications-registered')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('start-adding-medication')}
              </p>
              <button
                onClick={() => {
                  setEditingMedication(null)
                  setShowAddModal(true)
                }}
                className="btn btn-primary"
              >
                {t('add-medication')}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {medications.map((medication) => {
              const stockStatus = getStockStatus(medication)
              const stockColor = getStockColor(stockStatus)
              
              return (
                <div key={medication.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  {/* Header */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2 flex-1">
                        {/* Pill Image */}
                        <div className="w-8 h-8 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          {medication.pillSvg ? (
                            <div 
                              dangerouslySetInnerHTML={{
                                __html: medication.pillSvg
                                  .replace('width="60"', 'width="20"')
                                  .replace('height="60"', 'height="20"')
                              }}
                            />
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                            </svg>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {medication.name}
                          </h3>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            {medication.dosage}mg
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {/* Stock indicator */}
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${stockColor}`}>
                          {(medication.currentStock || medication.stock || 0)}/{(medication.maxStock || 30)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 space-y-2">
                    {/* Schedule */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('schedules')}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {formatTimes(medication.times)}
                      </p>
                    </div>

                    {/* Days */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('days')}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDays(medication.daysOfWeek)}
                      </p>
                    </div>

                    {/* Stock status */}
                    {stockStatus === 'low' && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-1.5">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                          ⚠️ {t('low-stock')}
                        </p>
                      </div>
                    )}
                    {stockStatus === 'empty' && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-1.5">
                        <p className="text-xs text-red-800 dark:text-red-200">
                          🚨 {t('no-stock')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => {
                          const currentStock = medication.currentStock || medication.stock || 0
                          const maxStock = medication.maxStock || 30
                          setNewCurrentStock(currentStock.toString())
                          setNewMaxStock(maxStock.toString())
                          setShowStockModal(medication.id)
                        }}
                        className="text-xs text-primary hover:text-primary/80 font-medium"
                      >
                        {t('adjust')}
                      </button>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(medication)}
                          className="text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                        >
                          {t('edit')}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(medication.id)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Medication Modal */}
      <AddMedicationModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        editingMedication={editingMedication}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('confirm-deletion')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('delete-medication-confirm')}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockModal && (
        <div className="modal-overlay" onClick={() => setShowStockModal(null)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('update-stock')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('current-stock')}
                  </label>
                  <input
                    type="number"
                    value={newCurrentStock}
                    onChange={(e) => setNewCurrentStock(e.target.value)}
                    className="input"
                    placeholder={t('current-medication-amount')}
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('max-stock')}
                  </label>
                  <input
                    type="number"
                    value={newMaxStock}
                    onChange={(e) => setNewMaxStock(e.target.value)}
                    className="input"
                    placeholder={t('max-medication-amount')}
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('reason-optional')}
                  </label>
                  <input
                    type="text"
                    value={stockReason}
                    onChange={(e) => setStockReason(e.target.value)}
                    className="input"
                    placeholder={t('restock-correction')}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowStockModal(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => handleStockUpdate(showStockModal)}
                  className="btn btn-primary px-4 py-2"
                  disabled={!newCurrentStock || !newMaxStock}
                >
                  {t('update')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicationsPage 